// Aktionsmodul: Angebot beim Dienstleister anfordern (statt Direktbeauftragung).
// Gilt nur für Dienstleister mit beauftragung_modus === 'angebot' (strikte Gleichheit —
// null/undefined/'direkt' laufen über handwerkerBeauftragen). Text/Betreff-Aufbau ist an
// baueAuftragsMail() (lib/tickets/auftragsMail.ts) angelehnt, aber bittet um ein
// unverbindliches Angebot statt eine Direktbeauftragung.
import { supabase } from '../../supabase/client';
import type { AuftragsDaten, AuftragsMail } from '../../tickets/auftragsMail';
import type { AktionEntwurf, AktionKontext, AktionModul, TicketAktion } from '../types';
import { auftragsBetreffObjekt } from '../../tickets/auftragsMail';

const clean = (v: string | null | undefined) => (v && v.trim() ? v.trim() : '');
const mark = (v: string, feld: string) => (v ? v : `[bitte ergänzen: ${feld}]`);

function objektKurz(o: AuftragsDaten['objekt']): string {
  return [clean(o?.strasse), clean(o?.hausnummer)].filter(Boolean).join(' ');
}
function objektAdresse(o: AuftragsDaten['objekt']): string {
  const zeile1 = [clean(o?.strasse), clean(o?.hausnummer)].filter(Boolean).join(' ');
  const zeile2 = [clean(o?.plz), clean(o?.ort)].filter(Boolean).join(' ');
  return [zeile1, zeile2].filter(Boolean).join(', ');
}

export function baueAngebotsMail(d: AuftragsDaten): AuftragsMail {
  const kategorie = mark(clean(d.kategorie), 'Kategorie');
  const einheit = mark(clean(d.einheit), 'Einheit');
  const empfaenger = clean(d.dienstleister?.email);

  // #525: Objekt mit Ort, fehlende Einheit nicht als Marker im Betreff
  const betreff = `Angebotsanfrage – ${kategorie} – ${auftragsBetreffObjekt(d) || mark('', 'Objekt')}`;

  const eigentuemer = mark(clean(d.eigentuemer), 'Eigentümer / WEG');
  const text = [
    'Sehr geehrte Damen und Herren,',
    '',
    'Objektdaten',
    `Objekt: ${mark(objektAdresse(d.objekt), 'Objektadresse')}`,
    `Wohnung/Einheit: ${einheit}`,
    `Eigentümer / WEG: ${eigentuemer}`,
    '',
    'Gemeldeter Mangel',
    `Kategorie: ${kategorie}`,
    'Beschreibung:',
    mark(clean(d.mangelBeschreibung), 'Mangelbeschreibung'),
    '',
    'Kontaktdaten des Meldenden',
    `Name: ${mark(clean(d.meldender?.name), 'Name')}`,
    `Rolle: ${mark(clean(d.meldender?.rolle), 'Rolle')}`,
    `Telefon: ${mark(clean(d.meldender?.telefon), 'Telefon')}`,
    `E-Mail: ${mark(clean(d.meldender?.email), 'E-Mail')}`,
    '',
    'Bevor wir Sie mit der Behebung des Mangels beauftragen, bitten wir Sie zunächst um ein unverbindliches Angebot für die Behebung des beschriebenen Mangels. Bitte führen Sie Arbeiten erst nach unserer schriftlichen Beauftragung aus.',
    '',
    'Bitte senden Sie uns Ihr Angebot per E-Mail an info@immo-schwede.de.',
    '',
    'Vielen Dank für Ihre Unterstützung.',
    '',
    'Mit freundlichen Grüßen',
  ].join('\n');

  return { empfaenger, betreff, text };
}

export function erzeugen(ctx: AktionKontext): AktionEntwurf | null {
  const stammdaten = ctx.stammdaten;
  if (!stammdaten || !stammdaten.dienstleister) return null;
  const { dienstleister, auftrag, anhaenge } = stammdaten;

  if (dienstleister.beauftragung_modus !== 'angebot') return null;
  if (!dienstleister.email) return null;
  if (!auftrag) return null;

  const mail = baueAngebotsMail(auftrag);

  return {
    typ: 'angebot_anfordern',
    freigabe_erforderlich: true,
    ist_rechtlich_sensibel: false,
    empfaenger: mail.empfaenger,
    empfaenger_kontakt_id: dienstleister.id,
    betreff: mail.betreff,
    entwurf_text: mail.text,
    payload: { dienstleister_id: dienstleister.id, anhaenge },
    ki_confidence: ctx.ticket.ki_confidence ?? null,
  };
}

// Nachbereitung nach Mail-Versand (dünner Supabase-Wrapper, Konvention: nicht unit-getestet).
// Anders als bei handwerkerBeauftragen bleibt der Ticket-Status unverändert.
export async function nachVersand(aktion: TicketAktion, benutzerId: string | null, opts?: { emailVerknuepft?: boolean }): Promise<void> {
  await supabase.from('ticket_historie').insert({
    ticket_id: aktion.ticket_id,
    benutzer_id: benutzerId,
    aktion: 'angebot_angefordert',
    neuer_wert: `Angebot angefordert: ${aktion.empfaenger}`,
  });
  // Mail hängt als Thread-Eintrag am Ticket → kein Volltext-Spiegel-Kommentar mehr nötig.
  if (opts?.emailVerknuepft) return;
  await supabase.from('ticket_kommentare').insert({
    ticket_id: aktion.ticket_id,
    autor_id: benutzerId,
    inhalt: `Angebotsanfrage gesendet:\n\n${aktion.entwurf_text}`,
    ist_intern: true,
  });
}

export const angebotAnfordernModul: AktionModul = { typ: 'angebot_anfordern', erzeugen, nachVersand };
